import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { subscriptionAPI } from '../services/api';
import { useAuth } from './AuthContext';

interface Credit {
  currentBalance: number;
  monthlyCredit: number;
  totalCreditsUsed: number;
  nextRenewalDate?: Date;
}

interface CreditContextType {
  credit: Credit | null;
  loading: boolean;
  refreshCredit: () => Promise<void>;
}

const CreditContext = createContext<CreditContextType | undefined>(undefined);

export const CreditProvider = ({ children }: { children: ReactNode }) => {
  const { business } = useAuth();
  const [credit, setCredit] = useState<Credit | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshCredit = async () => {
    if (!business?._id) {
      setCredit(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await subscriptionAPI.getCredit(business._id);
      if (response.data?.credit) {
        setCredit({
          currentBalance: response.data.credit.currentBalance || 0,
          monthlyCredit: response.data.credit.monthlyCredit || 0,
          totalCreditsUsed: response.data.credit.totalCreditsUsed || 0,
          nextRenewalDate: response.data.credit.nextRenewalDate 
            ? new Date(response.data.credit.nextRenewalDate) 
            : undefined,
        });
      }
    } catch (error) {
      console.error('Failed to fetch credit:', error);
      setCredit(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (business?._id) {
      refreshCredit();
    } else {
      setCredit(null);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?._id]);

  const value: CreditContextType = {
    credit,
    loading,
    refreshCredit,
  };

  return (
    <CreditContext.Provider value={value}>
      {children}
    </CreditContext.Provider>
  );
};

export const useCredit = () => {
  const context = useContext(CreditContext);
  if (context === undefined) {
    throw new Error('useCredit must be used within a CreditProvider');
  }
  return context;
};

